import { z } from 'zod';

/**
 * A job's questions, answered from the fleet page (IMM-981).
 *
 * A run that may not make a decision parks: it writes the problem and its
 * options on the ticket (the fleet-agent toolkit (`park-options.ts`)) and ends its job
 * `parked`. The job now carries that same question, so an operator can answer
 * it on `admin.immoos.ai` instead of in a terminal. A job carries one or more
 * questions (IMM-917), and a live job's questions are read by the run that
 * asked them, which waits for the answer instead of parking.
 *
 * The backend never writes Linear. An answer is stored here and RELAYED by a
 * worker, which records it on the ticket exactly as `fleet-agent agent unblock` does
 * and drops `needs-human` — the label is what unparks the ticket, so the
 * relay is the only step that changes what a lane will do.
 */
export const FLEET_QUESTION_MAX_OPTIONS = 12;
export const FLEET_QUESTION_PROBLEM_MAX_CHARS = 2_000;
export const FLEET_QUESTION_OPTION_MAX_CHARS = 1_000;
export const FLEET_QUESTION_ANSWER_MAX_CHARS = 4_000;
/** A product TL;DR: two plain sentences and a slice title, never a document (OPS-87). */
export const FLEET_QUESTION_BRIEF_MAX_CHARS = 600;
/** How long a run that asked mid-flight waits before it parks (IMM-917). One number for the whole fleet. */
export const FLEET_QUESTION_MID_FLIGHT_WAIT_MS = 5 * 60 * 1000;
export const FLEET_QUESTION_MAX_PER_ASK = 12;

/** One choice the park recorded — `park-options.ts`'s `ParkOption`, as the wire carries it. */
export const FleetQuestionOptionSchema = z.object({
  /** The letter or number to answer with, lowercased: `a`, `b`, `1`. */
  key: z.string().min(1).max(8),
  text: z.string().min(1).max(FLEET_QUESTION_OPTION_MAX_CHARS),
  recommended: z.boolean(),
});

export type FleetQuestionOption = z.infer<typeof FleetQuestionOptionSchema>;

export const FLEET_QUESTION_ANSWERER_KINDS = ['operator', 'jev', 'model'] as const;
export const FleetQuestionAnswererKindSchema = z.enum(FLEET_QUESTION_ANSWERER_KINDS);
export type FleetQuestionAnswererKind = z.infer<typeof FleetQuestionAnswererKindSchema>;

/** Who an asking agent says a question is for. Unmarked questions go to the tech person. */
export const FLEET_QUESTION_AUDIENCES = ['product', 'tech'] as const;
export const FleetQuestionAudienceSchema = z.enum(FLEET_QUESTION_AUDIENCES);
export type FleetQuestionAudience = z.infer<typeof FleetQuestionAudienceSchema>;

/** Lowercase on purpose: a wire value, like `MESSENGERS`. `null` on the wire is the fleet page or the decider ladder. */
export const FLEET_QUESTION_ANSWER_CHANNELS = ['whatsapp', 'telegram'] as const;
export const FleetQuestionAnswerChannelSchema = z.enum(FLEET_QUESTION_ANSWER_CHANNELS);
export type FleetQuestionAnswerChannel = z.infer<typeof FleetQuestionAnswerChannelSchema>;

/** What the worker's decider ladder made of a question before recording it (IMM-917). */
export const FleetQuestionDeciderOutcomeSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('answered'),
    by: z.enum(['jev', 'model']),
    answer: z.string().min(1).max(FLEET_QUESTION_ANSWER_MAX_CHARS),
    confidence: z.number().min(0).max(1),
  }),
  z.object({ outcome: z.literal('declined') }),
]);

export type FleetQuestionDeciderOutcome = z.infer<typeof FleetQuestionDeciderOutcomeSchema>;

/** What a worker attaches to a `parked` end. Options may be empty: a park that recorded none takes a typed answer. */
export const FleetJobQuestionInputSchema = z.object({
  problem: z.string().min(1).max(FLEET_QUESTION_PROBLEM_MAX_CHARS),
  options: z.array(FleetQuestionOptionSchema).max(FLEET_QUESTION_MAX_OPTIONS),
  decider: FleetQuestionDeciderOutcomeSchema.optional(),
  audience: FleetQuestionAudienceSchema.optional(),
  /**
   * What the ticket does for the user and which slice is asking (OPS-87), shown above the question so the
   * operator need not look the ticket up. Derived by the worker from the PRD and slice doc; absent when none.
   */
  brief: z.string().min(1).max(FLEET_QUESTION_BRIEF_MAX_CHARS).optional(),
});

export type FleetJobQuestionInput = z.infer<typeof FleetJobQuestionInputSchema>;

export const AskFleetJobQuestionsBodySchema = z.object({
  claimEpoch: z.number().int().positive(),
  questions: z.array(FleetJobQuestionInputSchema).min(1).max(FLEET_QUESTION_MAX_PER_ASK),
});

export type AskFleetJobQuestionsBody = z.infer<typeof AskFleetJobQuestionsBodySchema>;

export const ReadFleetJobQuestionsBodySchema = z.object({ claimEpoch: z.number().int().positive() });

export type ReadFleetJobQuestionsBody = z.infer<typeof ReadFleetJobQuestionsBodySchema>;

export const FleetRunAnswerSchema = z.object({
  text: z.string(),
  answeredBy: z.string(),
  answererKind: FleetQuestionAnswererKindSchema,
  confidence: z.number().min(0).max(1).nullable(),
  answeredAt: z.iso.datetime(),
  answeredVia: FleetQuestionAnswerChannelSchema.nullable(),
});

export type FleetRunAnswer = z.infer<typeof FleetRunAnswerSchema>;

/** A question as the run that asked it reads it back. */
export const FleetRunQuestionSchema = z.object({
  id: z.uuid(),
  askedAt: z.iso.datetime(),
  /** Until when the run waits; `null` once answered. */
  waitingUntil: z.iso.datetime().nullable(),
  answer: FleetRunAnswerSchema.nullable(),
});

export type FleetRunQuestion = z.infer<typeof FleetRunQuestionSchema>;

export const FleetRunQuestionsResponseSchema = z.object({ data: z.array(FleetRunQuestionSchema) });

export type FleetRunQuestionsResponse = z.infer<typeof FleetRunQuestionsResponseSchema>;

export const FleetQuestionPathParamsSchema = z.object({ questionId: z.uuid() });

/**
 * An answer given on the fleet page that no worker has carried to the ticket
 * yet. Shown rather than hidden: the relay runs only inside a lane daemon, so
 * with the fleet stopped an answer can wait indefinitely, and a question that
 * vanished on submit would read as "done" while the ticket stays parked.
 */
export const FleetQuestionPendingAnswerSchema = z.object({
  text: z.string(),
  answeredBy: z.string(),
  answeredAt: z.iso.datetime(),
  answererKind: FleetQuestionAnswererKindSchema,
  confidence: z.number().min(0).max(1).nullable(),
  answeredVia: FleetQuestionAnswerChannelSchema.nullable(),
});

export type FleetQuestionPendingAnswer = z.infer<typeof FleetQuestionPendingAnswerSchema>;

/**
 * The operator's view of a pending question: not yet on the ticket, and its job
 * still the latest round on that key. Once any later round claims the key (the
 * ticket was unparked some other way) the question is moot and drops off —
 * answered or not. `answer` is `null` while it waits for an operator, and set
 * while it waits for a worker to record it.
 */
/**
 * How often a work item (`IMM-815-engineer`) has gone round, derived from its
 * job rows — one row per claim. `blocked` counts rounds that ended `parked`;
 * `unblocked` counts the parked ones a later round picked back up.
 */
export const FleetJobKeyHistorySchema = z.object({
  runs: z.number().int().min(0),
  blocked: z.number().int().min(0),
  unblocked: z.number().int().min(0),
});

export type FleetJobKeyHistory = z.infer<typeof FleetJobKeyHistorySchema>;

export const FleetQuestionViewSchema = z.object({
  id: z.uuid(),
  jobId: z.uuid(),
  /** The work item, e.g. `IMM-815-engineer`. */
  key: z.string(),
  /** The Linear ticket the key names, e.g. `IMM-815`. */
  ticket: z.string(),
  worker: z.string(),
  machineName: z.string(),
  parkedAt: z.iso.datetime(),
  askedAt: z.iso.datetime(),
  askedDuringRun: z.boolean(),
  /** Whether the job that asked is still live — its run is still waiting, not parked. */
  jobLive: z.boolean(),
  /** Until when the run waits; `null` for a parked question and once answered. */
  waitingUntil: z.iso.datetime().nullable(),
  /** The ticket's product TL;DR (OPS-87); `null` on questions asked without one. */
  brief: z.string().nullable(),
  problem: z.string(),
  options: z.array(FleetQuestionOptionSchema),
  answer: FleetQuestionPendingAnswerSchema.nullable(),
  /** The decider ladder was offered the question and could not answer it. */
  deciderDeclined: z.boolean(),
  /** The worker this question's job stays bound to, once its wait elapsed; `null` otherwise. */
  boundWorker: z.string().nullable(),
  history: FleetJobKeyHistorySchema,
});

export type FleetQuestionView = z.infer<typeof FleetQuestionViewSchema>;

export const ListFleetQuestionsResponseSchema = z.object({ data: z.array(FleetQuestionViewSchema) });

export type ListFleetQuestionsResponse = z.infer<typeof ListFleetQuestionsResponseSchema>;

/**
 * The operator's answer, word for word. A picked option is sent as its text
 * prefixed with its key (`(a) …`) by the page — the backend does not interpret
 * it, exactly as `unblock` does not.
 */
export const AnswerFleetQuestionBodySchema = z.object({
  answer: z.string().trim().min(1).max(FLEET_QUESTION_ANSWER_MAX_CHARS),
});

export type AnswerFleetQuestionBody = z.infer<typeof AnswerFleetQuestionBodySchema>;

export const AnswerFleetQuestionResponseSchema = z.object({
  id: z.uuid(),
  answeredAt: z.iso.datetime(),
});

export type AnswerFleetQuestionResponse = z.infer<typeof AnswerFleetQuestionResponseSchema>;

/**
 * A machine reads a ticket's still-open questions (the `/feature-unblock` walk). The same view the fleet page lists,
 * narrowed to the ticket and to questions nobody has answered yet.
 */
export const ListOpenFleetQuestionsBodySchema = z.object({
  /** The Linear ticket, e.g. `IMM-815`. */
  ticket: z.string().trim().min(1).max(32),
});

export type ListOpenFleetQuestionsBody = z.infer<typeof ListOpenFleetQuestionsBodySchema>;

export const FLEET_QUESTION_ANSWERED_BY_MAX_CHARS = 120;

/**
 * A person answers at a fleet machine's terminal (`agent unblock`) instead of on the fleet page. Stored exactly as an
 * operator's answer; `answeredBy` is the name the relay attributes the decision comment to, since a machine has no
 * admin behind it.
 */
export const AnswerFleetQuestionAsMachineBodySchema = AnswerFleetQuestionBodySchema.extend({
  answeredBy: z.string().trim().min(1).max(FLEET_QUESTION_ANSWERED_BY_MAX_CHARS),
});

export type AnswerFleetQuestionAsMachineBody = z.infer<typeof AnswerFleetQuestionAsMachineBodySchema>;

/**
 * The fleet assistant records an answer the operator gave in the chat (IMM-1420). The backend attributes it to the
 * turn's fleet person and channel, so the body names no answerer: the turn is the only way a machine speaks for a person.
 */
export const AnswerFleetQuestionForAssistantTurnBodySchema = AnswerFleetQuestionBodySchema.extend({
  assistantTurnId: z.uuid(),
});

export type AnswerFleetQuestionForAssistantTurnBody = z.infer<typeof AnswerFleetQuestionForAssistantTurnBodySchema>;

/** The turn variant is first: a body carrying both `assistantTurnId` and `answeredBy` is a turn answer, `answeredBy` stripped. */
export const AnswerFleetQuestionFromMachineBodySchema = z.union([
  AnswerFleetQuestionForAssistantTurnBodySchema,
  AnswerFleetQuestionAsMachineBodySchema,
]);

export type AnswerFleetQuestionFromMachineBody = z.infer<typeof AnswerFleetQuestionFromMachineBodySchema>;

/**
 * "Ask again" (OPS-79): the question goes to the front of the operator's messenger queue and out next,
 * prefixed "still open since". Nothing to send: the question itself is the content.
 */
export const AskFleetQuestionAgainBodySchema = z.object({});

export const AskFleetQuestionAgainResponseSchema = z.object({ id: z.uuid() });

export type AskFleetQuestionAgainResponse = z.infer<typeof AskFleetQuestionAgainResponseSchema>;

/**
 * One answer a worker must carry to Linear. Leased to the calling machine for
 * {@link FLEET_QUESTION_RELAY_LEASE_MS}: a worker that dies mid-relay leaves it
 * to the next one rather than stranding it, and two workers never hold it at once.
 */
export const FLEET_QUESTION_RELAY_LEASE_MS = 2 * 60 * 1000;

export const FleetAnswerToRelaySchema = z.object({
  id: z.uuid(),
  key: z.string(),
  ticket: z.string(),
  problem: z.string(),
  answer: z.string(),
  /** Who answered, as the decision comment attributes it. */
  answeredBy: z.string(),
  answeredAt: z.iso.datetime(),
  answererKind: FleetQuestionAnswererKindSchema,
  confidence: z.number().min(0).max(1).nullable(),
  /** `true` when no other question on this job is still open — only then may the relay drop `needs-human`. */
  unparks: z.boolean(),
});

export type FleetAnswerToRelay = z.infer<typeof FleetAnswerToRelaySchema>;

export const TakeFleetAnswersBodySchema = z.object({});

export const TakeFleetAnswersResponseSchema = z.object({ data: z.array(FleetAnswerToRelaySchema) });

export type TakeFleetAnswersResponse = z.infer<typeof TakeFleetAnswersResponseSchema>;

export const MarkFleetAnswerRelayedBodySchema = z.object({});

export const MarkFleetAnswerRelayedResponseSchema = z.object({ relayedAt: z.iso.datetime() });

export type MarkFleetAnswerRelayedResponse = z.infer<typeof MarkFleetAnswerRelayedResponseSchema>;
